/**
 * Демо-сцена героя — РАСКЛАДКА КАК ДАННЫЕ.
 *
 * До рефакторинга координаты зон были рассыпаны по 551 строке JSX, рёбра между
 * ними стояли вручную, а кадры камеры подбирались на глаз. Поменять композицию
 * было нельзя: любой сдвиг зоны рвал рёбра и осколки финала.
 *
 * Теперь единственный источник правды — таблица `ZONES` ниже. Из неё выводятся
 * рёбра (по якорям, а не по вбитым координатам), кадры камеры (по объединению
 * зон плана) и позиция финала. Менять композицию = править таблицу.
 *
 * ── Что показывает сцена ────────────────────────────────────────────────────
 * Идея «одного окна»: все продуктовые окна живут ВНУТРИ рамки PRONA, агент —
 * СНАРУЖИ и общается через MCP. Сквозных артефактов два — документ «Корзина: как
 * устроено сейчас» и задача SHOP-214; каждый присутствует в нескольких окнах
 * сразу, и от каждого вхождения пунктир уходит вниз в узел хранения: это не
 * копии, а один Knowledge Object в трёх представлениях (граф / вектор /
 * пространство) — §4.3 архитектурной концепции Semantic Workspace.
 *
 * ── Жёсткое ограничение ─────────────────────────────────────────────────────
 * Зону `task` можно ПЕРЕНОСИТЬ, но НЕЛЬЗЯ МАСШТАБИРОВАТЬ: осколки финала
 * (OUTRO_PARTS) — абсолютные координаты деталей внутри карточки 340×458, снятые
 * с живого DOM. При переносе зоны их сдвигают на ту же дельту,
 * при изменении размера — пришлось бы переснимать таблицу целиком.
 */

export type Zone = {
  /** Левый верхний угол в координатах мира. */
  x: number;
  y: number;
  w: number;
  h: number;
  /** Человеческое имя — идёт в шапку окна и в отладку. */
  title: string;
  /** Внутри рамки PRONA (продукт) или снаружи (агент, хранилище). */
  inside: boolean;
  /**
   * Знак раздела — с ГЛИФОМ РАЗДЕЛА, а не с осьминогом.
   *
   * Прежде окна носили фавиконки продукта: знак там один на все разделы, и
   * различал их только цвет подложки — в анимации, где окна мелькают, этого
   * мало. Теперь на подложке того же цвета стоит глиф самого раздела: тот же,
   * что в переключателе разделов в шапке сайдбара, в кнопке «Доска
   * планирования» и в ленте разделов на главной. Один знак в четырёх местах.
   *
   * Файлы `*-section-chrome.svg` лежат в public. У Доски знак СВОЙ и не имеет
   * отношения к одноимённому самостоятельному продукту PRONA Board: у того своя
   * фиолетовая айдентика и своя фавиконка, путать их нельзя.
   */
  icon?: string | null;
  /** Акцент раздела — из палитры variables.scss, для точки и подписи. */
  accent?: string;
};

/**
 * Мир сцены. КОМПАКТНЫЙ и почти квадратный: всё живёт вплотную к доске.
 * Раньше мир был 2260 в ширину, и участнику приходилось «идти» через всю сцену
 * до Базы знаний — переход выглядел долгой дорогой. Теперь разделы открываются
 * панелью у края доски, поэтому мир схлопнулся, а масштабы всех планов легли в
 * узкую полосу 0.96…1.23: переезды камеры читаются как короткие панорамы.
 */
export const WORLD = { w: 2320, h: 1320 } as const;

/**
 * Рамка PRONA — граница «одного окна». Всё продуктовое внутри неё, агент снаружи.
 * Это главный носитель смысла в сцене: без рамки окна читаются как разные
 * приложения, с рамкой — как разделы одного.
 */
/**
 * Рамка PRONA. ВЫСОТА ПОСТОЯННАЯ: все окна лежат в один ряд, и тянуть рамку
 * вниз незачем — чат открывается панелью ПОВЕРХ доски, а не под ней. Пока чат
 * стоял отдельным окном снизу, рамке приходилось растягиваться и схлопываться,
 * а узел хранения оказывался в полуметре пустоты под содержимым.
 * Ширина же меняется одним тактом: справа рамка ужимается до страницы задачи,
 * когда бэклог закрыт (см. такт `grow` с `ws`).
 */
export const FRAME = { x: 40, y: 60, w: 1860, h: 700 } as const;

/**
 * Зоны.
 *
 * Композиция теперь ОДНООКОННАЯ: доска занимает всю рабочую область, а разделы
 * открываются панелью справа от неё — по одному за раз, в одном и том же слоте.
 * Так задумано в продукте (панель плагинов рядом с рабочим полотном), и так
 * видно главное: нужный раздел под рукой, переход мгновенный.
 *
 * Размер `task` = 340×458 ЗАФИКСИРОВАН осколками финала — см. шапку файла.
 * Переносить зону можно, менять размер — нет.
 */
export const ZONES = {
  /** Рабочая доска команды — вся область. */
  board: { x: 120, y: 120, w: 880, h: 580, title: 'Доска', inside: true,
    icon: '/prona-board-section-chrome.svg', accent: '#884960' },
  /**
   * Доска хода работы — ОТДЕЛЬНАЯ и ПОД СТРАНИЦЕЙ ЗАДАЧИ, а не на месте рабочей.
   * Агент заводит её сам, наполняет, и она схлопывается в задачу вложением —
   * ради этого движения она и стоит здесь: слева по кромке с задачей, снизу,
   * чтобы «улетела наверх, в список артефактов» читалось одним жестом.
   * Пока она открывалась поверх рабочей доски, схлопывать было некуда и не
   * видно было, что это НОВАЯ доска, а не та же самая.
   *
   * Под неё рамка PRONA временно растёт вправо и вниз (такты `grow`).
   */
  board2: { x: 1040, y: 700, w: 880, h: 500, title: 'SHOP-214 · ход работы агента', inside: true,
    icon: '/prona-board-section-chrome.svg', accent: '#884960' },

  /* ── Панели правого слота: показываются по одной ─────────────────────── */
  kb: { x: 1040, y: 120, w: 360, h: 470, title: 'База знаний', inside: true,
    icon: '/prona-knowledge-section-chrome.svg', accent: '#b08a3e' },
  /**
   * Чат — ПРАВЫЙ САЙДБАР ДОСКИ: вызывается кнопкой в правом нижнем углу канвы и
   * выезжает вдоль правой кромки на всю высоту рабочей области. Так это и
   * устроено в продукте: переписка не уводит с полотна, а пристыковывается
   * сбоку. Отдельное окно под доской заставляло тянуть рамку вниз и уводило
   * камеру с доски ровно в тот момент, когда «нашёл в чате → написал на доске»
   * должно читаться одним движением.
   *
   * Координаты СОВПАДАЮТ с рабочей областью доски (её контент начинается на
   * 150 и кончается на 700), с отступом в 2px справа: сайдбар — сосед окна, а
   * не его ребёнок, и без отступа его прямой угол вылезал бы за скруглённую
   * кромку доски.
   */
  chat: { x: 638, y: 152, w: 360, h: 546, title: 'Чат команды', inside: true,
    icon: '/prona-chat-section-chrome.svg', accent: '#5b6b80' },
  task: { x: 1040, y: 150, w: 340, h: 458, title: 'SHOP-214', inside: true,
    icon: '/prona-tasks-section-chrome.svg', accent: '#5454a1' },
  /**
   * У бэклога СВОЙ слот справа от задачи, а не общий с ней: их надо показать
   * рядом — задача создалась, и в бэклоге она тоже есть. В одном слоте это
   * пришлось бы разносить по времени, и связь между окнами терялась.
   */
  backlog: { x: 1420, y: 120, w: 440, h: 560, title: 'Бэклог', inside: true,
    icon: '/prona-tasks-section-chrome.svg', accent: '#5454a1' },

  /**
   * Узел хранения — под рамкой, по центру трёх вхождений задачи. Он появляется
   * ровно на один такт: после того как зритель увидел синюю обводку на карточке
   * доски, странице задачи и строке бэклога, пунктиры сводят их в одну запись и
   * называют её. Дальше узел уходит — объяснение нужно один раз.
   */
  store: { x: 690, y: 800, w: 620, h: 130, title: 'Knowledge Object', inside: false },

  /**
   * Агент — СНАРУЖИ рамки и КРУПНЫЙ: когда он подключается, окно выходит на
   * первый план справа. Единственная связь с продуктом идёт через MCP.
   */
  agent: { x: 1480, y: 150, w: 720, h: 540, title: 'Claude Code', inside: false },
} as const satisfies Record<string, Zone>;

export type ZoneKey = keyof typeof ZONES;

/** Шильдик MCP на правой кромке рамки — единственная точка входа агента. */
/**
 * Шильдик MCP — на УКОРОЧЕННОЙ правой кромке рамки (40 + 1380), а не на полной:
 * к моменту, когда агент подключается, рамка уже ужалась до страницы задачи.
 * Привязка к FRAME.w здесь была бы неверной — та описывает исходную ширину.
 */
export const MCP_GATE = { x: 1374, y: 402, w: 92, h: 36 } as const;

/**
 * «Один объект» — короткие связки между тремя видами ОДНОЙ задачи: карточкой на
 * доске, страницей задачи и строкой бэклога.
 *
 * Раньше на эту мысль работал отдельный узел хранения под сценой и четыре
 * длинных пунктира к нему. От него отказались: сцена теперь ПОКАЗЫВАЕТ то же
 * самое буквально — задачу создают из выделенных элементов доски, и она в тот
 * же миг возникает во всех трёх местах. Абстрактная схема поверх наглядной
 * демонстрации только отнимала внимание и требовала мелкого общего плана.
 * Осталась короткая подпись-связка, которая просто называет увиденное.
 */
/**
 * Пунктиры от трёх вхождений задачи к узлу хранения.
 * `railX` — вынос вбок перед спуском: прямой спуск из строки бэклога прошёл бы
 * сквозь весь список, поэтому линия уходит в зазор у кромки рамки.
 */
export const STORE_LINKS: { zone: ZoneKey; dx: number; dy: number; railX?: number }[] = [
  { zone: 'board', dx: 660, dy: 550 },
  { zone: 'task', dx: 170, dy: 458 },
  { zone: 'backlog', dx: 440, dy: 72, railX: 1880 },
];

export const cx = (z: Zone): number => z.x + z.w / 2;
export const cy = (z: Zone): number => z.y + z.h / 2;

/** Точка на кромке зоны — для рёбер: 'r' правая, 'l' левая, 't' верх, 'b' низ. */
export function edgePoint(z: Zone, side: 'l' | 'r' | 't' | 'b'): { x: number; y: number } {
  switch (side) {
    case 'l': return { x: z.x, y: cy(z) };
    case 'r': return { x: z.x + z.w, y: cy(z) };
    case 't': return { x: cx(z), y: z.y };
    default: return { x: cx(z), y: z.y + z.h };
  }
}

/** Габарит объединения нескольких зон — основа для кадра камеры. */
export function union(keys: ZoneKey[]): { x: number; y: number; w: number; h: number } {
  const zs = keys.map((k) => ZONES[k]);
  const x0 = Math.min(...zs.map((z) => z.x));
  const y0 = Math.min(...zs.map((z) => z.y));
  const x1 = Math.max(...zs.map((z) => z.x + z.w));
  const y1 = Math.max(...zs.map((z) => z.y + z.h));
  return { x: x0, y: y0, w: x1 - x0, h: y1 - y0 };
}

/**
 * Кадр камеры на прямоугольник мира: вписать с полями, не крупнее потолка.
 * Тот же метод, которым пересчитывались кадры прежней сцены — он проверен:
 * поля 6%, потолок приближения 1.30 при пределе сцены 1.35.
 */
export function shot(
  rect: { x: number; y: number; w: number; h: number },
  stage: { w: number; h: number },
  opts?: { pad?: number; max?: number },
): { tx: number; ty: number; s: number } {
  const pad = opts?.pad ?? 0.94;
  const max = opts?.max ?? 1.3;
  const s = Math.min(pad * Math.min(stage.w / rect.w, stage.h / rect.h), max);
  const mx = rect.x + rect.w / 2;
  const my = rect.y + rect.h / 2;
  return {
    tx: +(-(mx - stage.w / 2 / s)).toFixed(2),
    ty: +(-(my - stage.h / 2 / s)).toFixed(2),
    s: +s.toFixed(3),
  };
}

/** Прямоугольник рамки как цель для общего плана. */
export const FRAME_RECT = { x: FRAME.x, y: FRAME.y, w: FRAME.w, h: FRAME.h };
